<template>
  <div v-if="wallet" class="nav-item dropdown">
    <a
      class="nav-link dropdown-toggle"
      @click="state.accountDropdown = !state.accountDropdown"
      role="button"
    >
      Wallet: {{ getAccName() }}
    </a>
    <ul class="dropdown-menu show" v-if="state.accountDropdown">
      <li>
        <button class="dropdown-item" @click="copy(address)">
          Copy {{ shortAddress }}
        </button>
      </li>
      <li>
        <button class="dropdown-item" @click="disconnect">Disconnect</button>
      </li>
    </ul>
  </div>
  <div v-else class="nav-item">
    <a
      class="nav-link"
      href="#"
      @click="state.connectWalletModal = true"
      data-toggle="modal"
      data-target="#exampleModal"
      >Connect with Keplr</a
    >
  </div>
  <div
    class="modal fade"
    id="exampleModal"
    tabindex="-1"
    aria-labelledby="exampleModalLabel"
    aria-hidden="true"
  >
    <DysModal
      :visible="state.connectWalletModal"
      :close-icon="false"
      :cancel-button="false"
      :submit-button="false"
      style="text-align: center"
    >
      <template #header>
        <div v-if="state.modalPage === 'connect'">
          <h3 v-if="isKeplrAvailable">Connect your wallet</h3>
          <h3 v-else>Install Keplr</h3>
        </div>
        <div v-else-if="state.modalPage === 'connecting'">
          <div class="">Opening Keplr</div>
          <h3>Connecting</h3>
        </div>
        <div v-else-if="state.modalPage === 'error'">
          <SpWarningIcon style="margin-bottom: 20px" />
          <h3>Keplr cannot launch</h3>
        </div>
      </template>
      <template #body>
        <div style="max-width: 320px; text-align: center; margin: auto">
          <SpKeplrIcon />
          <div v-if="state.modalPage === 'connect'">
            <p v-if="isKeplrAvailable">
              Connect your Keplr wallet via the Keplr browser extension to use
              this app.
            </p>
            <p v-else>
              Install & connect your Keplr wallet via the Keplr browser
              extension to use this app.
            </p>
          </div>
          <div v-else-if="state.modalPage === 'connecting'">
            <div style="margin-top: 2rem">
              <SpSpinner />
            </div>
            <SpButton
              aria-label="Cancel"
              type="secondary"
              style="margin-top: 3rem"
              @click="state.modalPage = 'connect'"
            >
              Cancel
            </SpButton>
            <div class="" style="margin-top: 2rem">
              Having trouble opening Keplr?
            </div>
          </div>
          <div v-else-if="state.modalPage === 'error'" style="padding: 20px 0">
            Make sure Keplr is installed.
          </div>
        </div>
      </template>
      <template v-if="isKeplrAvailable" #footer>
        <div v-if="state.modalPage === 'connect'">
          <SpButton
            aria-label="Connect Keplr"
            type="primary"
            @click="tryToConnectToKeplr"
          >
            Connect Keplr
          </SpButton>
        </div>
        <div
          v-if="state.modalPage === 'error'"
          style="gap: 10px; display: flex; justify-content: center"
        >
          <SpButton
            aria-label="Connect Keplr"
            type="secondary"
            @click="state.connectWalletModal = false"
          >
            Cancel
          </SpButton>
          <SpButton
            aria-label="Connect Keplr"
            type="primary"
            @click="state.modalPage = 'connect'"
          >
            Try again
          </SpButton>
        </div>
      </template>
    </DysModal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import { useAddress, useClipboard } from '../../composables'
import useKeplr from '../../composables/useKeplr'
import { Wallet } from '../../utils/interfaces'
import SpButton from '../SpButton'
import SpChevronDownIcon from '../SpChevronDown'
import SpExternalArrowIcon from '../SpExternalArrow'
import SpKeplrIcon from '../SpKeplrIcon'
import DysModal from '../DysModal'
import SpProfileIcon from '../SpProfileIcon'
import SpSpinner from '../SpSpinner'
import SpWarningIcon from '../SpWarningIcon'

export interface State {
  modalPage: string
  connectWalletModal: boolean
  accountDropdown: boolean
  keplrParams: { name: string; bech32Address: string }
}

export let initialState: State = {
  modalPage: 'connect',
  connectWalletModal: false,
  accountdropdown: false,
  keplrParams: { name: '', bech32Address: '' }
}

export default defineComponent({
  name: 'DysAcc',

  components: {
    DysModal,
    SpButton,
    SpSpinner,
    SpProfileIcon,
    SpKeplrIcon,
    SpWarningIcon,
    SpExternalArrowIcon,
    SpChevronDownIcon
  },

  setup() {
    // $s
    let $s = useStore()

    // state
    let state = reactive(initialState)

    let { address, shortAddress } = useAddress({ $s })
    let { copy } = useClipboard()

    // composables
    let {
      connectToKeplr,
      isKeplrAvailable,
      getOfflineSigner,
      getKeplrAccParams,
      listenToAccChange
    } = useKeplr({ $s })

    // computed
    let wallet = computed<Wallet>(() => $s.getters['common/wallet/wallet'])
    let chainId = computed<string>(() => $s.getters['common/env/chainId'])

    // actions
    let signInWithKeplr = async (offlineSigner: any) =>
      $s.dispatch('common/wallet/connectWithKeplr', offlineSigner)
    let signOut = async () => $s.dispatch('common/wallet/signOut')

    let onKeplrConnect = async () => {
      let { name, bech32Address } = await getKeplrAccParams(chainId.value)
      state.keplrParams.name = name
      state.keplrParams.bech32Address = bech32Address

      let offlineSigner = getOfflineSigner(chainId.value)
      signInWithKeplr(offlineSigner)

      state.connectWalletModal = false
      state.modalPage = 'connect'

      $('#exampleModal').modal('hide')
    }
    listenToAccChange(onKeplrConnect)

    let onKeplrError = (): void => {
      state.modalPage = 'error'
    }

    // methods
    let tryToConnectToKeplr = (): void => {
      state.modalPage = 'connecting'
      localStorage.setItem('walletConnected', true)

      return connectToKeplr(onKeplrConnect, onKeplrError)
    }
    let getAccName = (): string => {
      if (wallet.value?.name === 'Keplr Integration') {
        return state.keplrParams?.name + ''
      } else {
        return wallet.value?.name + ''
      }
    }
    let disconnect = (): void => {
      state.accountDropdown = false
      localStorage.removeItem('walletConnected')
      signOut()
    }

    // check if already connected
    onMounted(() => {
      if (localStorage.getItem('walletConnected')) {
        tryToConnectToKeplr()
      }
    })

    return {
      address,
      shortAddress,
      // methods
      copy,
      isKeplrAvailable,
      tryToConnectToKeplr,
      disconnect,
      state,
      wallet,
      getAccName
    }
  }
})
</script>

<style scoped lang="scss"></style>
